import {observer} from "mobx-react";
import {AudioExampleModel, AudioFileModel, getAcrRatingLabels} from "../../../shared/models/AudioTestModel";
import React, {useContext, useEffect, useState} from "react";
import Grid from "@material-ui/core/Grid";
import {SurveyControlRender} from "../../forms/SurveyControl.render";
import {AudioButton, AudioController, useAudioPlayer} from "../../web-audio/AudiosPlayer";
import {AudioLoading, useAllAudioRefsReady} from "../../web-audio/AudiosLoading";
import {useRandomization} from "../../../shared/tools/RandomizationTools";
import {ratingAreaStyle, useMatStyles} from "../../../shared/SharedStyles";
import {AudioSectionLoopingController} from "../../web-audio/AudioSectionLoopingController";
import {Box, Collapse, Slider, Typography} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import {GlobalDialog} from "../../../shared/ReactContexts";
import {validatePlayedOnceError} from "../../../shared/validators/ErrorValidators";

export const AcrTestItemExampleRender = observer(function (props: { example: AudioExampleModel, active?: boolean, previewMode?: boolean, onAudioLoadingChange?: (loading: boolean) => void }) {
  const {example, active, previewMode, onAudioLoadingChange} = props;
  const classes = useMatStyles();
  // Randomize first to make sure random audio match the dom tree
  const [randomAudios, randomPattern] = useRandomization(example.medias, active && example.settings?.randomMedia, example.settings?.fixLastInternalQuestion);
  // This is a custom hook that expose some functions for AudioButton and Controller
  const {
    refs, sampleRef, activeRef, loopRange, setLoopRange, currentTime, handleTimeUpdate, handlePlay, handlePause, handleEnded, resetCurrentTime, handleSeek
  } = useAudioPlayer(randomAudios, example.mediaRef, example);
  const allRefs = example.mediaRef ? [...refs, sampleRef] : refs;
  const allAudios = example.mediaRef ? [...randomAudios, example.mediaRef] : randomAudios;
  const {loading, loadError, progress, sources, retry} = useAllAudioRefsReady(allRefs, allAudios, active !== false);
  // An event for setting Time update method
  const [onTimeUpdate, setOnTimeUpdate] = useState<() => void>();
  // Current activated internal questions
  const [currentIndex, setCurrentIndex] = useState(0);
  const openDialog = useContext(GlobalDialog);
  useEffect(() => {
    if (active === false) handlePause();
    // If there are more 1 internal question, block next question
    else if (example.medias.length > 1) example.blockNext = true;
  }, [active]);
  // To fix a warning that update state during rendering
  useEffect(() => {
    if (currentIndex >= randomAudios.length - 1) example.blockNext = false;
    // Delete placedOnce for next internal question
    delete example.playedOnce;
  }, [currentIndex]);

  const handleClickNext = () => {
    if (currentIndex < randomAudios.length) {
      // Validation for require full listening
      const error = validatePlayedOnceError(example);
      if (error && !previewMode) {
        openDialog(error);
        return;
      }
      handlePause();
      resetCurrentTime();
      // If new state is at end of internal question, the next question button will be un block
      setCurrentIndex(currentIndex + 1);
    }
  }

  return <>
    <AudioLoading showing={loading} error={loadError} progress={progress} onRetry={retry} onLoadingChange={onAudioLoadingChange}/>
    <Grid container spacing={2} style={{display: loading || loadError ? 'none' : 'flex'}}>
      {example.fields?.length === randomPattern.length ? randomPattern.map(
        // Use collapse as transition to give subjects an illusion
        (ri, i) => <Grid item key={i} xs={12} hidden={currentIndex !== i}>
          <Collapse in={currentIndex === i} timeout={{enter: 250, exit: 0}}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SurveyControlRender control={example.fields[ri]}/>
              </Grid>
              <Grid item>
                <AcrRatingBar audio={randomAudios[i]} labels={example.settings?.ratingLabels}/>
                <AudioButton ref={refs[i]} audio={randomAudios[i]} source={sources[randomAudios[i].src]} load={active !== false} onPlay={handlePlay} onPause={handlePause}
                             onEnded={handleEnded}
                             onTimeUpdate={() => {
                               handleTimeUpdate();
                               onTimeUpdate?.();
                             }}/>
              </Grid>
              {/*Reference*/}
              {example.mediaRef && <Grid item style={ratingAreaStyle}>
                <AudioButton ref={sampleRef} audio={example.mediaRef} source={sources[example.mediaRef.src]} load={active !== false} onPlay={handlePlay}
                             onPause={handlePause} onEnded={handleEnded}
                             onTimeUpdate={() => {
                               handleTimeUpdate();
                               onTimeUpdate?.();
                             }}>Ref</AudioButton>
              </Grid>}
              {/*Audio tracking bar*/}
              <Grid item xs={12}>
                <AudioController activeRef={activeRef} currentTime={currentTime} onSeek={handleSeek}
                                 disabled={example.settings?.disablePlayerSlider}/>
                {example.settings?.sectionLooping &&
                <AudioSectionLoopingController setTimeUpdate={f => setOnTimeUpdate(f)} audioRef={activeRef}
                                               currentTime={currentTime} range={loopRange} onRangeChange={setLoopRange}/>}
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
      ) : <Grid item xs={12}><Typography color="secondary">
        Task configuration has an error. If you are creator please delete this question and create a new one.
        This text only appears when the test is out of date.
      </Typography></Grid>}

      {example.blockNext && <Grid item xs={12} className={classes.flexEnd}>
        <Button color="primary" onClick={handleClickNext} disabled={example.fields?.length !== randomPattern.length}>Next</Button>
      </Grid>}
    </Grid>
  </>
});

export const AcrRatingBar = observer(function ({audio, labels: configuredLabels}: { audio: AudioFileModel, labels?: string[] }) {
  const labels = getAcrRatingLabels(configuredLabels);
  const max = labels.length;
  const defaultValue = Math.ceil(max / 2);
  const parsedValue = Number(audio.value);
  const value = Number.isInteger(parsedValue) && parsedValue >= 1 && parsedValue <= max ? parsedValue : defaultValue;
  const marks = labels.map((label, index) => ({value: index + 1, label: `${index + 1} - ${label}`}));

  useEffect(() => {
    if (audio.value !== value.toString()) audio.value = value.toString();
  });

  return <Box ml={2.5} mb={2} mt={2} style={{height: 200}}>
    <Slider orientation="vertical" aria-labelledby="vertical-slider" min={1} max={max} step={1} track={false}
            getAriaValueText={(value: number) => `${value}`} marks={marks}
            value={value}
            onChange={(_, value) => audio.value = value.toString()}/>
  </Box>
});
